import { calculerScores, dateCourte } from "@repo/utils";
import { useMemo } from "react";
import { telechargerCsv } from "./csv";
import type {
  EvaluationData,
  EvaluationWithScores,
} from "./CampagneResultatsTab";

export function useCampagneResultats(
  evaluations: EvaluationData[] | undefined,
  campagneNom: string | undefined,
) {
  const evaluationsWithScores = useMemo(() => {
    if (!evaluations) return [];
    return evaluations.map(
      (evaluation: EvaluationData): EvaluationWithScores => {
        const scores = evaluation.questionsEtat
          ? calculerScores(evaluation.questionsEtat)
          : null;
        return {
          ...evaluation,
          scores,
          globalPercentage:
            scores && scores.scoreGlobalMax > 0
              ? Math.round(
                  (scores.scoreGlobalObtenu / scores.scoreGlobalMax) * 100,
                )
              : 0,
        };
      },
    );
  }, [evaluations]);

  const allCompetences = useMemo(() => {
    if (evaluationsWithScores.length === 0) return [];
    const first = evaluationsWithScores.find((e) => e.scores);
    if (!first?.scores) return [];
    return first.scores.competences.filter((c) => c.nom !== "Question neutre");
  }, [evaluationsWithScores]);

  const allCompetencesForExport = useMemo(() => {
    if (evaluationsWithScores.length === 0) return [];
    const first = evaluationsWithScores.find((e) => e.scores);
    return first?.scores?.competences ?? [];
  }, [evaluationsWithScores]);

  // L'export suit l'ordre des données, plus celui de l'écran : le tri vit
  // maintenant dans le `DataTable`, qui ne le fait pas remonter — et un CSV
  // ouvert dans un tableur se retrie de toute façon d'un clic.
  const handleExportCSV = () => {
    if (!campagneNom || evaluationsWithScores.length === 0) return;

    const headers = [
      "Candidat",
      "Date",
      ...allCompetencesForExport.flatMap((c) => [
        `${c.nom} (Obtenu)`,
        `${c.nom} (Max)`,
      ]),
      "Score Global (%)",
    ];

    const rows = evaluationsWithScores.map((evaluation) => {
      const scoresMap = new Map(
        evaluation.scores?.competences.map((c) => [c.nom, c]) ?? [],
      );
      return [
        `${evaluation.prenomCandidat} ${evaluation.nomCandidat}`,
        evaluation.dateFin ? dateCourte(evaluation.dateFin) : "",
        ...allCompetencesForExport.flatMap((comp) => {
          const score = scoresMap.get(comp.nom);
          return score
            ? [String(score.scoreObtenu), String(score.scoreMax)]
            : ["", ""];
        }),
        `${evaluation.globalPercentage}`,
      ];
    });

    telechargerCsv(campagneNom, headers, rows);
  };

  return {
    evaluationsWithScores,
    allCompetences,
    handleExportCSV,
  };
}
